import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { MuiTheme } from "../styles/MuiDataTableStyle";
import { useStateAuthValue } from "../context/AuthState";
import { useToast } from "../context/Toast/ToastProvider";
import { showToast, ToastType } from "../utils/ToastUtil";
import { LoadingModal } from "../components/Modals/LoadingModal";
import {
  createBinBlacklist,
  deleteBinBlacklist,
  getBinBlacklist,
  updateBinBlacklist,
} from "../services/binBlacklistService";
import EditIcon from "@mui/icons-material/Edit";
import MUIDataTable from "mui-datatables";
import { downloadOptions, textLabels } from "../utils/muiTableOptions";
import { Delete, Search } from "@mui/icons-material";
import { ClearIcon } from "@mui/x-date-pickers";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { BaseModal } from "../components/Modals/BaseModal";
import PageHeader from "../components/PageHeader";

const EMPTY_BIN = { bin: "", brand: "", reason: "", isActive: true };

const BinBlacklist = () => {
  const [{ userToken }] = useStateAuthValue();
  const [data, setData] = useState([]);
  const [binSelected, setBinSelected] = useState(EMPTY_BIN);
  const [error, setError] = useState();
  const [openCreateNewModal, setOpenCreateNewModal] = useState(false);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [textSearch, setTextSearch] = useState("");
  const [lastTextSearch, setLastTextSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const toastDispatch = useToast();

  const displayError = (msg) =>
    showToast(toastDispatch, msg, ToastType.ERROR, ToastType.ERROR);
  const displaySuccess = (msg) =>
    showToast(toastDispatch, msg, ToastType.SUCCESS, ToastType.SUCCESS);

  const getData = async (brand = "") => {
    const filters = brand ? { brand } : {};
    const response = await getBinBlacklist(userToken, filters);
    const rows = Array.isArray(response?.content)
      ? response.content
      : Array.isArray(response)
      ? response
      : [];
    setData(rows);
  };

  const isEditing = Boolean(binSelected.bin && data.find((d) => d.bin === binSelected.bin));

  const saveData = async () => {
    if (isEditing) {
      const body = {
        brand: binSelected.brand,
        reason: binSelected.reason,
        isActive: binSelected.isActive,
      };
      const res = await updateBinBlacklist(userToken, binSelected.bin, body);
      if (res?.status === 200) {
        displaySuccess("BIN actualizado correctamente");
        getData("");
        setOpenCreateNewModal(false);
      } else {
        displayError(
          res?.response?.data ?? "Error al actualizar, intente más tarde"
        );
        setError(res?.response?.data ?? "Error al actualizar");
      }
    } else {
      const body = {
        bin: binSelected.bin,
        brand: binSelected.brand,
        reason: binSelected.reason,
        isActive: binSelected.isActive,
      };
      const res = await createBinBlacklist(userToken, body);
      if (res?.status === 200) {
        displaySuccess("BIN agregado correctamente");
        getData("");
        setOpenCreateNewModal(false);
      } else {
        displayError(
          res?.response?.data ?? "Error al guardar, intente más tarde"
        );
        setError(res?.response?.data ?? "Error al guardar");
      }
    }
  };

  const deleteData = async () => {
    const res = await deleteBinBlacklist(userToken, binSelected.bin);
    if (res?.status === 200) {
      displaySuccess("BIN eliminado correctamente");
      getData("");
    } else {
      displayError("Error al eliminar el BIN");
    }
    setOpenConfirmDeleteModal(false);
  };

  useEffect(() => {
    setLoading(true);
    getData("").finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      getData(textSearch?.trim());
      setLastTextSearch(textSearch?.trim());
    }
  };

  const customSearchRender = (searchText, handleSearch) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      <IconButton
        onClick={() => {
          getData(textSearch?.trim());
          setLastTextSearch(textSearch?.trim());
        }}
      >
        <Search />
      </IconButton>
      <TextField
        variant="standard"
        value={searchText}
        onChange={(event) => handleSearch(event.target.value)}
        onKeyDown={handleSearchKeyDown}
        placeholder="Buscar por marca..."
        InputProps={{ "aria-label": "Buscar" }}
      />
      <IconButton
        onClick={() => {
          handleSearch("");
          setLastTextSearch("");
          getData("");
        }}
      >
        <ClearIcon />
      </IconButton>
      {lastTextSearch && lastTextSearch.trim() !== "" && (
        <Box
          sx={{
            background: "linear-gradient(195deg, #002C54, #003f77)",
            color: "white",
            fontWeight: "bold",
            padding: "10px",
            borderRadius: "15px",
          }}
        >
          {lastTextSearch}
        </Box>
      )}
    </div>
  );

  const columns = [
    { name: "bin", label: "BIN", options: { filter: true, sort: true } },
    { name: "brand", label: "Marca", options: { filter: true, sort: true } },
    { name: "reason", label: "Motivo", options: { filter: true, sort: false } },
    {
      name: "blockedAt",
      label: "Bloqueado el",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          if (!value) return "-";
          const d = new Date(value);
          const day = d.getDate().toString().padStart(2, "0");
          const month = (d.getMonth() + 1).toString().padStart(2, "0");
          const year = d.getFullYear();
          const hours = d.getHours().toString().padStart(2, "0");
          const minutes = d.getMinutes().toString().padStart(2, "0");
          return `${day}/${month}/${year} ${hours}:${minutes}`;
        },
      },
    },
    {
      name: "isActive",
      label: "Activo",
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value) => (value ? "Sí" : "No"),
      },
    },
    {
      name: "acciones",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => (
          <div className="icons-container">
            <EditIcon
              className="icon edit-icon"
              onClick={() => {
                setError();
                setBinSelected(data[dataIndex]);
                setOpenCreateNewModal(true);
              }}
            />
            <Delete
              className="icon delete-icon"
              onClick={() => {
                setBinSelected(data[dataIndex]);
                setOpenConfirmDeleteModal(true);
              }}
            />
          </div>
        ),
      },
    },
  ];

  const options = {
    searchOpen: true,
    selectableRows: "none",
    textLabels,
    downloadOptions,
    filterType: "dropdown",
    responsive: "standard",
    draggableColumns: { enabled: false },
    enableNestedDataAccess: ".",
    serverSide: true,
    pagination: false,
    elevation: 0,
    customSearchRender,
    onSearchChange: (searchQuery) => {
      if (searchQuery == null) setTextSearch("");
    },
    onTableChange: (action, tableState) => {
      if (action === "search") setTextSearch(tableState.searchText);
    },
    customToolbar: () => (
      <Tooltip
        title="Agregar BIN"
        onClick={() => {
          setError();
          setBinSelected(EMPTY_BIN);
          setOpenCreateNewModal(true);
        }}
      >
        <IconButton>
          <AddCircleIcon />
        </IconButton>
      </Tooltip>
    ),
  };

  return (
    <ThemeProvider theme={MuiTheme}>
      <div className="users">
        <div className="users-table-container">
          <PageHeader title="Lista Negra de BINs" />
          <ThemeProvider theme={MuiTheme}>
            <Box
              sx={{
                width: "100%",
                "& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-head": {
                  span: { justifyContent: "center" },
                },
              }}
            >
              <MUIDataTable
                title=""
                data={data}
                columns={columns}
                options={options}
              />
            </Box>
          </ThemeProvider>
        </div>
      </div>

      {loading && <LoadingModal />}

      {/* Modal crear / editar */}
      <BaseModal open={openCreateNewModal} setOpen={setOpenCreateNewModal}>
        <Box
          sx={{
            padding: "10px",
            borderRadius: "15px",
            color: "black",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            width: "100%",
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: "700", letterSpacing: "1px" }}>
            {isEditing ? "Editar BIN" : "Agregar BIN"}
          </Typography>

          {!isEditing && (
            <TextField
              label="BIN (6 dígitos)"
              variant="standard"
              value={binSelected.bin}
              inputProps={{ maxLength: 6 }}
              onChange={(e) => {
                setError();
                const val = e.target.value.replace(/\D/g, "");
                setBinSelected({ ...binSelected, bin: val });
              }}
            />
          )}

          <TextField
            label="Marca (ej: VISA)"
            variant="standard"
            value={binSelected.brand ?? ""}
            onChange={(e) => {
              setError();
              setBinSelected({ ...binSelected, brand: e.target.value });
            }}
          />

          <TextField
            label="Motivo"
            variant="standard"
            value={binSelected.reason ?? ""}
            onChange={(e) => {
              setError();
              setBinSelected({ ...binSelected, reason: e.target.value });
            }}
          />

          {isEditing && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={binSelected.isActive ?? true}
                  onChange={(e) =>
                    setBinSelected({ ...binSelected, isActive: e.target.checked })
                  }
                />
              }
              label="Activo"
            />
          )}

          {error && (
            <p style={{ color: "red", fontWeight: "bold" }}>{error}</p>
          )}

          <Button
            variant="contained"
            onClick={() => {
              if (!isEditing && (!binSelected.bin || !/^\d{6}$/.test(binSelected.bin))) {
                setError("El BIN debe ser exactamente 6 dígitos numéricos.");
                return;
              }
              saveData();
            }}
          >
            GUARDAR
          </Button>
        </Box>
      </BaseModal>

      {/* Diálogo confirmar eliminar */}
      <Dialog
        open={openConfirmDeleteModal}
        onClose={() => setOpenConfirmDeleteModal(false)}
        style={{ borderRadius: "10px" }}
      >
        <DialogTitle>{"¿Eliminar BIN de la lista?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se eliminará el BIN <strong>{binSelected.bin}</strong> permanentemente. ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDeleteModal(false)}>
            Cancelar
          </Button>
          <Button onClick={deleteData}>Continuar</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default BinBlacklist;
